import { LTLQuote } from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import { Undef } from 'types/UtilityTypes';

type RateMetadataRecord = Record<string, unknown>;

export function getFiniteNumber(value: unknown): Undef<number> {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return undefined;
}

function getNormalizedQuoteSource(quote: LTLQuote): string {
  const source = (quote as unknown as { source?: unknown }).source;
  return typeof source === 'string' ? source.trim().toLowerCase() : '';
}

function hasTaiStyleRateMetadata(metadata: Undef<RateMetadataRecord>): boolean {
  return Boolean(
    metadata &&
    getFiniteNumber(metadata.buyRateTotal) !== undefined &&
    getFiniteNumber(metadata.sellRateTotal) !== undefined
  );
}

function hasThreeGStyleRateMetadata(
  metadata: Undef<RateMetadataRecord>
): boolean {
  return Boolean(
    metadata &&
    (getFiniteNumber(metadata.netTotal) !== undefined ||
      getFiniteNumber(metadata.netCost) !== undefined ||
      metadata.isThreeG === true)
  );
}

function hasTurvoStyleRateMetadata(
  metadata: Undef<RateMetadataRecord>
): boolean {
  return Boolean(
    metadata &&
    (typeof metadata.pricingType === 'string' ||
      getFiniteNumber(metadata.payable) !== undefined ||
      getFiniteNumber(metadata.billable) !== undefined ||
      metadata.isTurvo === true)
  );
}

export function normalizeQuote(quote: LTLQuote): LTLQuote {
  // Note: backend may return gorm.Model ID as "ID" (capitalized), while LTLQuote expects "id".
  const rawId =
    (quote as unknown as { id?: number | string }).id ??
    (quote as unknown as { ID?: number | string }).ID;
  const id = Number(rawId);

  const rawRateMetadata = quote.rateMetadata as
    Record<string, unknown> | undefined;
  const normalizedSource = getNormalizedQuoteSource(quote);

  const hasTaiRateMetadata = hasTaiStyleRateMetadata(rawRateMetadata);
  const hasThreeGRateMetadata = hasThreeGStyleRateMetadata(rawRateMetadata);
  const hasTurvoRateMetadata = hasTurvoStyleRateMetadata(rawRateMetadata);

  // Prefer source-specific checks when source is available; otherwise
  // fallback to shape detection for backwards compatibility.
  let hasKnownSourceRateMetadata: boolean;
  switch (normalizedSource) {
    case 'tai':
      hasKnownSourceRateMetadata = hasTaiRateMetadata;
      break;
    case 'threeg':
    case '3g':
      hasKnownSourceRateMetadata = hasThreeGRateMetadata;
      break;
    case 'turvo':
      hasKnownSourceRateMetadata = hasTurvoRateMetadata;
      break;
    default:
      hasKnownSourceRateMetadata =
        hasTaiRateMetadata || hasThreeGRateMetadata || hasTurvoRateMetadata;
  }

  const normalizedRateMetadata = hasKnownSourceRateMetadata
    ? {
        ...(quote.rateMetadata ?? {}),
        isTurvo: hasTurvoRateMetadata ? true : quote.rateMetadata?.isTurvo,
        payable:
          quote.rateMetadata?.payable == null
            ? undefined
            : (getFiniteNumber(quote.rateMetadata.payable) ?? 0),
        billable:
          quote.rateMetadata?.billable == null
            ? undefined
            : (getFiniteNumber(quote.rateMetadata.billable) ?? 0),
        margin:
          quote.rateMetadata?.margin == null
            ? undefined
            : (getFiniteNumber(quote.rateMetadata.margin) ?? 0),
        transitDays:
          quote.rateMetadata?.transitDays == null
            ? undefined
            : (getFiniteNumber(quote.rateMetadata.transitDays) ?? 0),
      }
    : undefined;

  return {
    ...quote,
    id: Number.isFinite(id) ? id : 0,
    totalCost: getFiniteNumber(quote.totalCost) ?? 0,
    transitDays: getFiniteNumber(quote.transitDays) ?? 0,
    distance:
      quote.distance == null
        ? undefined
        : (getFiniteNumber(quote.distance) ?? 0),
    coverageAmountNew:
      quote.coverageAmountNew == null
        ? undefined
        : (getFiniteNumber(quote.coverageAmountNew) ?? 0),
    coverageAmountUsed:
      quote.coverageAmountUsed == null
        ? undefined
        : (getFiniteNumber(quote.coverageAmountUsed) ?? 0),
    rateMetadata: normalizedRateMetadata,
  };
}
